/**
 * FileTabs Component
 *
 * Displays open file tabs with support for:
 * - Tab switching (click to activate)
 * - Tab closing (click X or middle-click)
 * - Modified indicator (dot on unsaved files)
 * - Context menu (close, close others, close all)
 * - Horizontal scrolling when tabs overflow
 * - File type icons
 */

import { useCallback, useState, useRef, useEffect, memo } from 'react';
import { useFileStore, OpenFile, FileViewerType } from '@/stores/fileStore';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  X,
  FileText,
  FileCode,
  FileSpreadsheet,
  FileImage,
  FileType,
  File,
  ChevronLeft,
  ChevronRight,
  Loader2,
} from 'lucide-react';

// ============================================
// Types
// ============================================

interface FileTabProps {
  file: OpenFile;
  isActive: boolean;
  viewerType: FileViewerType;
  onActivate: () => void;
  onClose: () => void;
  onContextMenu: (e: React.MouseEvent) => void;
}

interface ContextMenuState {
  isOpen: boolean;
  x: number;
  y: number;
  filePath: string | null;
}

// ============================================
// Helper Functions
// ============================================

/**
 * Get the appropriate icon for a file type
 */
function getFileIcon(viewerType: FileViewerType) {
  switch (viewerType) {
    case 'code':
      return FileCode;
    case 'csv':
    case 'parquet':
      return FileSpreadsheet;
    case 'markdown':
      return FileText;
    case 'image':
      return FileImage;
    case 'pdf':
      return FileType;
    case 'text':
      return FileText;
    default:
      return File;
  }
}

// ============================================
// FileTab Component
// ============================================

const FileTab = memo(function FileTab({
  file,
  isActive,
  viewerType,
  onActivate,
  onClose,
  onContextMenu,
}: FileTabProps) {
  const Icon = getFileIcon(viewerType);

  const handleClose = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onClose();
    },
    [onClose]
  );

  const handleMiddleClick = useCallback(
    (e: React.MouseEvent) => {
      // Middle mouse button
      if (e.button === 1) {
        e.preventDefault();
        onClose();
      }
    },
    [onClose]
  );

  return (
    <div
      role="tab"
      aria-selected={isActive}
      tabIndex={isActive ? 0 : -1}
      className={cn(
        'group flex items-center gap-1.5 px-3 py-2 border-r border-surface-200 dark:border-surface-800 cursor-pointer select-none',
        'transition-colors duration-100',
        isActive
          ? 'bg-white dark:bg-surface-900 text-surface-900 dark:text-surface-100'
          : 'bg-surface-100 dark:bg-surface-800/50 text-surface-600 dark:text-surface-400 hover:bg-surface-50 dark:hover:bg-surface-800'
      )}
      onClick={onActivate}
      onMouseDown={handleMiddleClick}
      onContextMenu={onContextMenu}
    >
      {/* File icon */}
      {file.isLoading ? (
        <Loader2 className="h-4 w-4 shrink-0 animate-spin text-surface-400" />
      ) : (
        <Icon className="h-4 w-4 shrink-0" />
      )}

      {/* File name */}
      <span className="text-sm truncate max-w-32">{file.name}</span>

      {/* Modified indicator */}
      {file.isModified && (
        <span
          className="w-2 h-2 rounded-full bg-primary-500 shrink-0"
          title="Unsaved changes"
        />
      )}

      {/* Close button */}
      <button
        onClick={handleClose}
        className={cn(
          'p-0.5 rounded hover:bg-surface-200 dark:hover:bg-surface-700',
          'opacity-0 group-hover:opacity-100',
          'transition-opacity duration-100',
          isActive && 'opacity-100'
        )}
        aria-label={`Close ${file.name}`}
        title={`Close ${file.name}`}
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
});

// ============================================
// Context Menu Component
// ============================================

interface TabContextMenuProps {
  state: ContextMenuState;
  onClose: () => void;
  onCloseTab: () => void;
  onCloseOthers: () => void;
  onCloseAll: () => void;
}

function TabContextMenu({
  state,
  onClose,
  onCloseTab,
  onCloseOthers,
  onCloseAll,
}: TabContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu on outside click
  useEffect(() => {
    if (!state.isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [state.isOpen, onClose]);

  if (!state.isOpen) return null;

  return (
    <div
      ref={menuRef}
      className="fixed z-50 min-w-40 py-1 bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 rounded-lg shadow-lg"
      style={{ left: state.x, top: state.y }}
      role="menu"
    >
      <button
        className="w-full px-3 py-1.5 text-left text-sm text-surface-700 dark:text-surface-300 hover:bg-surface-100 dark:hover:bg-surface-700"
        onClick={() => {
          onCloseTab();
          onClose();
        }}
        role="menuitem"
      >
        Close
      </button>
      <button
        className="w-full px-3 py-1.5 text-left text-sm text-surface-700 dark:text-surface-300 hover:bg-surface-100 dark:hover:bg-surface-700"
        onClick={() => {
          onCloseOthers();
          onClose();
        }}
        role="menuitem"
      >
        Close Others
      </button>
      <button
        className="w-full px-3 py-1.5 text-left text-sm text-surface-700 dark:text-surface-300 hover:bg-surface-100 dark:hover:bg-surface-700"
        onClick={() => {
          onCloseAll();
          onClose();
        }}
        role="menuitem"
      >
        Close All
      </button>
    </div>
  );
}

// ============================================
// FileTabs Component
// ============================================

export function FileTabs() {
  const {
    openFiles,
    activeFilePath,
    setActiveFile,
    closeFile,
    closeOtherFiles,
    closeAllFiles,
    getViewerType,
  } = useFileStore();

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [contextMenu, setContextMenu] = useState<ContextMenuState>({
    isOpen: false,
    x: 0,
    y: 0,
    filePath: null,
  });

  // Check scroll position
  const checkScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    setCanScrollLeft(container.scrollLeft > 0);
    setCanScrollRight(
      container.scrollLeft < container.scrollWidth - container.clientWidth - 1
    );
  }, []);

  // Update scroll buttons on mount and resize
  useEffect(() => {
    checkScroll();

    const container = scrollContainerRef.current;
    if (!container) return;

    container.addEventListener('scroll', checkScroll);
    const resizeObserver = new ResizeObserver(checkScroll);
    resizeObserver.observe(container);

    return () => {
      container.removeEventListener('scroll', checkScroll);
      resizeObserver.disconnect();
    };
  }, [checkScroll, openFiles.length]);

  // Scroll handlers
  const scrollLeft = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.scrollBy({ left: -150, behavior: 'smooth' });
  }, []);

  const scrollRight = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.scrollBy({ left: 150, behavior: 'smooth' });
  }, []);

  // Context menu handler
  const handleContextMenu = useCallback((e: React.MouseEvent, filePath: string) => {
    e.preventDefault();
    setContextMenu({
      isOpen: true,
      x: e.clientX,
      y: e.clientY,
      filePath,
    });
  }, []);

  const closeContextMenu = useCallback(() => {
    setContextMenu((prev) => ({ ...prev, isOpen: false }));
  }, []);

  // Don't render if no files are open
  if (openFiles.length === 0) {
    return null;
  }

  return (
    <>
      <div
        className="flex items-center bg-surface-100 dark:bg-surface-800 border-b border-surface-200 dark:border-surface-700"
        role="tablist"
        aria-label="Open files"
      >
        {/* Scroll left button */}
        {canScrollLeft && (
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-8 shrink-0 rounded-none border-r border-surface-200 dark:border-surface-700"
            onClick={scrollLeft}
            aria-label="Scroll tabs left"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
        )}

        {/* Tabs container */}
        <div
          ref={scrollContainerRef}
          className="flex-1 flex overflow-x-auto scrollbar-hide"
        >
          {openFiles.map((file) => (
            <FileTab
              key={file.path}
              file={file}
              isActive={file.path === activeFilePath}
              viewerType={getViewerType(file.path)}
              onActivate={() => setActiveFile(file.path)}
              onClose={() => closeFile(file.path)}
              onContextMenu={(e) => handleContextMenu(e, file.path)}
            />
          ))}
        </div>

        {/* Scroll right button */}
        {canScrollRight && (
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-8 shrink-0 rounded-none border-l border-surface-200 dark:border-surface-700"
            onClick={scrollRight}
            aria-label="Scroll tabs right"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Context menu */}
      <TabContextMenu
        state={contextMenu}
        onClose={closeContextMenu}
        onCloseTab={() => {
          if (contextMenu.filePath) {
            closeFile(contextMenu.filePath);
          }
        }}
        onCloseOthers={() => {
          if (contextMenu.filePath) {
            closeOtherFiles(contextMenu.filePath);
          }
        }}
        onCloseAll={closeAllFiles}
      />
    </>
  );
}

export default FileTabs;
